// =============================================================================
// staticSite/pageRenderer — SiteModel + motyw + wariacja -> lista plikow.
//
// Wyjscie: /index.html, /<sciezka>/index.html per strona, /assets/<prefiks>.css,
// /sitemap.xml, /robots.txt, /404.html, /feed/index.xml, favicony (jesli
// przekazane), /_redirects, /_headers. Tresc artykulu = final_html VERBATIM
// (jedyna zmiana: sciezki obrazow po re-hostingu). Czyste stringi, zero DOM.
// =============================================================================

import type { ZipEntry } from "@/lib/zipStore";
import { buildHeadSnippet, normalizeHexColor } from "@/lib/faviconBuilder";
import type { PageModel, RenderedFile, SiteModel, StaticRedirectRow } from "./types";
import { getTheme, type RenderCtx, type Theme } from "./themes";
import { variationFromSeed, hueFromHex, seedForDomain, type Variation } from "./themes/variation";
import { rewriteAssetUrls, contentTypeForPath } from "./assetCollector";
import { renderRedirectsFile, renderHeadersFile } from "./redirectsBuilder";
import { absoluteUrl, encodePath, filePathForUrlPath } from "./urlPolicy";
import { esc, isoDate, sortByDateDesc, excerptFromHtml } from "./themes/shared";

export interface RenderOptions {
  themeId: string;
  /** sha256 hex domeny (static_sites.theme_seed). Brak = liczony z domeny. */
  themeSeed?: string | null;
  /** src -> sciezka lokalna. Pusta mapa = zostaw oryginalne URL-e obrazow (podglad przed deployem). */
  assetMap?: Map<string, string>;
  /** Pliki faviconow z buildFaviconBundle (przegladarka). Brak = bez faviconow. */
  faviconFiles?: ZipEntry[] | null;
  redirects: StaticRedirectRow[];
  /** Data generacji (deterministyczne testy). */
  now?: Date;
}

export interface RenderResult {
  files: RenderedFile[];
  variation: Variation;
  theme: Theme;
  warnings: string[];
}

const enc = new TextEncoder();

function textFile(path: string, text: string): RenderedFile {
  return { path, bytes: enc.encode(text), contentType: contentTypeForPath(path), text };
}

const FAVICON_ROOT_FILES = new Set([
  "logo.svg", "favicon.svg", "favicon.ico", "apple-touch-icon.png", "icon-192.png", "icon-512.png", "site.webmanifest",
]);

export function buildVariation(site: SiteModel, themeSeed?: string | null): Variation {
  const seed = themeSeed && /^[0-9a-f]{64}$/i.test(themeSeed) ? themeSeed : seedForDomain(site.domain);
  const brandHue = hueFromHex(site.brand?.themeColor);
  return variationFromSeed(seed, brandHue);
}

export function renderSite(site: SiteModel, opts: RenderOptions): RenderResult {
  const warnings: string[] = [];
  const theme = getTheme(opts.themeId);
  const v = buildVariation(site, opts.themeSeed);
  const assetMap = opts.assetMap || new Map<string, string>();
  const cssPath = `/assets/${v.classPrefix.replace(/-$/, "")}.css`;
  const now = opts.now || new Date();

  // Obrazy w tresci -> sciezki lokalne (tylko atrybuty obrazkowe)
  const pages: PageModel[] = site.pages.map((pg) => ({
    ...pg,
    bodyHtml: assetMap.size ? rewriteAssetUrls(pg.bodyHtml, assetMap, site.baseUrl) : pg.bodyHtml,
  }));
  const renderSiteModel: SiteModel = { ...site, pages };

  const headExtras = opts.faviconFiles?.length
    ? buildHeadSnippet(normalizeHexColor(site.brand?.themeColor))
    : "";

  const ctx: RenderCtx = { site: renderSiteModel, v, cssPath, headExtras, assetMap };
  const files: RenderedFile[] = [];
  const seen = new Set<string>();
  const push = (f: RenderedFile) => {
    if (seen.has(f.path)) { warnings.push(`Duplikat sciezki: ${f.path} (pominieto)`); return; }
    seen.add(f.path);
    files.push(f);
  };

  // CSS
  push(textFile(cssPath, theme.css(v, site.brand)));

  // Home
  push(textFile("/index.html", theme.renderers.home(ctx)));

  // Strony
  for (const page of pages) {
    if (page.kind === "home" || page.kind === "not_found") continue;
    const filePath = filePathForUrlPath(page.path);
    let html: string;
    switch (page.kind) {
      case "article":
      case "pillar":
      case "legacy_post":
        html = theme.renderers.article(ctx, page);
        break;
      case "author":
        html = theme.renderers.author(ctx, page);
        break;
      case "category": {
        const cat = site.categories.find((c) => c.path === page.path);
        html = cat
          ? theme.renderers.category(ctx, cat, pages.filter((pg) => pg.categorySlug === cat.slug && (pg.kind === "article" || pg.kind === "pillar" || pg.kind === "legacy_post")))
          : theme.renderers.page(ctx, page);
        break;
      }
      default:
        html = theme.renderers.page(ctx, page);
    }
    push(textFile(filePath, html));
  }

  // Kategorie bez wlasnego PageModel
  for (const cat of site.categories) {
    const filePath = filePathForUrlPath(cat.path);
    if (seen.has(filePath)) continue;
    const inCat = pages.filter((pg) => pg.categorySlug === cat.slug && (pg.kind === "article" || pg.kind === "pillar" || pg.kind === "legacy_post"));
    if (!inCat.length) continue;
    push(textFile(filePath, theme.renderers.category(ctx, cat, inCat)));
  }

  // 404
  push(textFile("/404.html", theme.renderers.notFound(ctx)));

  // Sitemap
  push(textFile("/sitemap.xml", renderSitemap(renderSiteModel, files, now)));

  // RSS
  push(textFile("/feed/index.xml", renderRss(renderSiteModel, ctx, now)));

  // robots
  push(textFile("/robots.txt", renderRobots(renderSiteModel, v)));

  // Favicony
  if (opts.faviconFiles?.length) {
    for (const f of opts.faviconFiles) {
      if (!FAVICON_ROOT_FILES.has(f.name)) continue;
      push({ path: `/${f.name}`, bytes: f.bytes, contentType: contentTypeForPath(f.name) });
    }
  }

  // _redirects + _headers
  const red = renderRedirectsFile(opts.redirects, site.domain);
  warnings.push(...red.warnings);
  push(textFile("/_redirects", red.content));
  push(textFile("/_headers", renderHeadersFile()));

  return { files, variation: v, theme, warnings };
}

function renderSitemap(site: SiteModel, files: RenderedFile[], now: Date): string {
  const entries: Array<{ loc: string; lastmod: string; priority: string }> = [];
  entries.push({ loc: absoluteUrl(site.baseUrl, "/"), lastmod: now.toISOString(), priority: "1.0" });
  for (const pg of site.pages) {
    if (pg.kind === "home" || pg.kind === "not_found") continue;
    entries.push({
      loc: absoluteUrl(site.baseUrl, encodePath(pg.path)),
      lastmod: isoDate(pg.dateModified || pg.datePublished) || now.toISOString(),
      priority: pg.kind === "pillar" ? "0.9" : pg.kind === "article" || pg.kind === "legacy_post" ? "0.8" : "0.5",
    });
  }
  const pagePaths = new Set(site.pages.map((pg) => pg.path));
  for (const cat of site.categories) {
    if (pagePaths.has(cat.path)) continue;
    if (!files.some((f) => f.path === filePathForUrlPath(cat.path))) continue;
    entries.push({ loc: absoluteUrl(site.baseUrl, encodePath(cat.path)), lastmod: now.toISOString(), priority: "0.6" });
  }
  const body = entries
    .map((e) => `  <url>\n    <loc>${esc(e.loc)}</loc>\n    <lastmod>${e.lastmod}</lastmod>\n    <priority>${e.priority}</priority>\n  </url>`)
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${body}\n</urlset>\n`;
}

function renderRss(site: SiteModel, ctx: RenderCtx, now: Date): string {
  const posts = sortByDateDesc(site.pages.filter((pg) => pg.kind === "article" || pg.kind === "pillar" || pg.kind === "legacy_post")).slice(0, 20);
  const items = posts
    .map((pg) => {
      const link = absoluteUrl(site.baseUrl, encodePath(pg.path));
      const desc = pg.metaDescription || pg.excerpt || excerptFromHtml(pg.bodyHtml);
      const date = pg.datePublished ? new Date(pg.datePublished).toUTCString() : now.toUTCString();
      return `    <item>\n      <title>${esc(pg.title)}</title>\n      <link>${esc(link)}</link>\n      <guid isPermaLink="true">${esc(link)}</guid>\n      <pubDate>${date}</pubDate>\n      <description>${esc(desc)}</description>\n    </item>`;
    })
    .join("\n");
  void ctx;
  return `<?xml version="1.0" encoding="UTF-8"?>\n<rss version="2.0">\n  <channel>\n    <title>${esc(site.siteName)}</title>\n    <link>${esc(site.baseUrl)}/</link>\n    <description>${esc(site.settings.tagline || site.siteName)}</description>\n    <language>${esc((site.language || "pl").slice(0, 2))}</language>\n    <lastBuildDate>${now.toUTCString()}</lastBuildDate>\n${items}\n  </channel>\n</rss>\n`;
}

function renderRobots(site: SiteModel, v: Variation): string {
  // Dwa warianty struktury (footprint) — wybor z seeda
  const variant = parseInt(v.seed.slice(12, 14), 16) % 2;
  const lines = variant === 0
    ? ["User-agent: *", "Allow: /", "", `Sitemap: ${site.baseUrl}/sitemap.xml`]
    : ["User-agent: *", "Disallow: /assets/", "Allow: /", "", `Sitemap: ${site.baseUrl}/sitemap.xml`, `Host: ${site.domain}`];
  return `${lines.join("\n")}\n`;
}
